import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortedRows, SortableHead } from "@/components/SortableHeader";
import { Pencil, Trash2, Car } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ACCESSORS = { date: (t) => t.date, miles: (t) => t.miles, deduction: (t) => t.deduction };

export const TripTable = ({ data, onEdit, onDelete }) => {
  const { sorted: rows, sort, toggle } = useSortedRows(data.trips, ACCESSORS, "date", "desc");
  return (
    <div className="bg-white rounded-xl shadow-lg border-0 overflow-hidden" data-testid="trip-table">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b bg-slate-50/60 text-sm">
        <span className="text-slate-600" data-testid="trip-count">{data.count} trip{data.count === 1 ? "" : "s"}</span>
        <div className="flex gap-4"><span>Miles <b data-testid="trip-total-miles">{(data.total_miles || 0).toFixed(1)}</b></span><span>Deduction <b className="text-emerald-700" data-testid="trip-total-deduction">{money(data.total_deduction)}</b></span></div>
      </div>
      <div className="md:hidden divide-y" data-testid="trip-card-list">
        {rows.length === 0 && <p className="py-10 text-center text-slate-500" data-testid="trip-card-empty">No trips yet — log your first job trip.</p>}
        {rows.map((t) => (
          <div key={t.id} className="p-3" data-testid="trip-card">
            <div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="font-medium text-slate-800 truncate">{t.purpose}</p><p className="text-xs text-slate-500">{t.date}{t.customer_name ? ` · ${t.customer_name}` : ""}</p>
              <p className="text-xs text-slate-500 truncate">{[t.from_location, t.to_location].filter(Boolean).join(" → ")}{t.round_trip ? " · round trip" : ""}</p></div>
              <div className="text-right whitespace-nowrap"><p className="font-semibold">{t.miles.toFixed(1)} mi</p><p className="text-sm font-semibold text-emerald-700">{money(t.deduction)}</p></div></div>
            <div className="flex items-center justify-between mt-1"><Badge variant="outline" className="font-mono text-[10px]">${t.rate.toFixed(3)}/mi</Badge>
              <div className="flex"><Button size="sm" variant="ghost" onClick={() => onEdit(t)} data-testid="trip-card-edit"><Pencil className="w-4 h-4" /></Button><Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(t)} data-testid="trip-card-delete"><Trash2 className="w-4 h-4" /></Button></div></div>
          </div>
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <SortableHead label="Date" sortKey="date" sort={sort} onToggle={toggle} />
          <TableHead>Trip</TableHead>
          <TableHead>Customer</TableHead>
          <SortableHead label="Miles" sortKey="miles" sort={sort} onToggle={toggle} align="right" />
          <TableHead className="text-right">Rate</TableHead>
          <SortableHead label="Deduction" sortKey="deduction" sort={sort} onToggle={toggle} align="right" />
          <TableHead className="w-24" />
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10" data-testid="trip-empty"><Car className="w-8 h-8 mx-auto mb-2 text-slate-300" />No trips yet — log your first job trip.</TableCell></TableRow>}
          {rows.map((t) => (
            <TableRow key={t.id} data-testid="trip-row">
              <TableCell className="whitespace-nowrap">{t.date}</TableCell>
              <TableCell>
                <p className="font-medium text-slate-800" data-testid="trip-row-purpose">{t.purpose}</p>
                <p className="text-xs text-slate-500">{[t.from_location, t.to_location].filter(Boolean).join(" → ") || (t.entry_mode === "odometer" ? `Odometer ${t.odometer_start} → ${t.odometer_end}` : "")}{t.round_trip ? " · round trip" : ""}{t.vehicle ? ` · ${t.vehicle}` : ""}</p>
              </TableCell>
              <TableCell className="text-slate-600">{t.customer_name || "—"}</TableCell>
              <TableCell className="text-right font-semibold" data-testid="trip-row-miles">{t.miles.toFixed(1)}</TableCell>
              <TableCell className="text-right text-slate-500"><Badge variant="outline" className="font-mono text-xs">${t.rate.toFixed(3)}</Badge></TableCell>
              <TableCell className="text-right font-semibold text-emerald-700" data-testid="trip-row-deduction">{money(t.deduction)}</TableCell>
              <TableCell><div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => onEdit(t)} data-testid="trip-edit-button"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(t)} data-testid="trip-delete-button"><Trash2 className="w-4 h-4" /></Button>
              </div></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
};
